// src/utils/firebaseMigration.js
import { collection, doc, setDoc, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import toast from 'react-hot-toast';

/**
 * Firebase Migration Utility
 * Helps migrate data from localStorage to Firebase and vice versa
 */
class FirebaseMigration {
  constructor() {
    this.collections = [
      'clients',
      'appointments',
      'invoices',
      'expenses',
      'payslips',
      'staff',
      'settings',
      'teamMessages'
    ];
  }

  /**
   * Migrate all data from localStorage to Firebase
   */
  async migrateToFirebase() {
    if (!db) {
      throw new Error('Firebase is not initialized. Please check your configuration.');
    }

    const results = {
      success: [],
      errors: [],
      totalMigrated: 0
    };

    toast.loading('Starting Firebase migration...', { id: 'migration' });

    try {
      for (const collectionName of this.collections) {
        try {
          const localData = this.getLocalStorageData(collectionName);

          if (localData && localData.length > 0) {
            const migrated = await this.migrateCollection(collectionName, localData);
            results.success.push({
              collection: collectionName,
              count: migrated
            });
            results.totalMigrated += migrated;
          } else {
            console.log(`No data found for ${collectionName}`);
          }
        } catch (error) {
          console.error(`Error migrating ${collectionName}:`, error);
          results.errors.push({
            collection: collectionName,
            error: error.message
          });
        }
      }

      // Handle settings separately (single document)
      await this.migrateSettings();

      toast.success(`Migration completed! ${results.totalMigrated} records migrated.`, {
        id: 'migration',
        duration: 5000
      });

      return results;
    } catch (error) {
      toast.error(`Migration failed: ${error.message}`, { id: 'migration' });
      throw error;
    }
  }

  /**
   * Migrate a single collection from localStorage to Firebase
   */
  async migrateCollection(collectionName, data) {
    let migratedCount = 0;
    const batch = [];

    for (const item of data) {
      try {
        const docRef = doc(collection(db, collectionName), item.id || doc(collection(db, collectionName)).id);
        batch.push(setDoc(docRef, {
          ...item,
          migratedAt: new Date(),
          migratedFrom: 'localStorage'
        }));

        // Process in batches of 50 to avoid Firebase limits
        if (batch.length >= 50) {
          await Promise.all(batch);
          batch.length = 0;
          migratedCount += 50;
        }
      } catch (error) {
        console.error(`Error migrating item ${item.id} in ${collectionName}:`, error);
      }
    }

    // Process remaining items
    if (batch.length > 0) {
      await Promise.all(batch);
      migratedCount += batch.length;
    }

    console.log(`Migrated ${migratedCount} items to ${collectionName}`);
    return migratedCount;
  }

  /**
   * Migrate settings (single document)
   */
  async migrateSettings() {
    try {
      const settings = localStorage.getItem('clinicSettings');
      if (settings) {
        const settingsData = JSON.parse(settings);
        const settingsRef = doc(db, 'settings', 'clinic_settings');
        await setDoc(settingsRef, {
          ...settingsData,
          migratedAt: new Date(),
          migratedFrom: 'localStorage'
        });
        console.log('Settings migrated successfully');
      }
    } catch (error) {
      console.error('Error migrating settings:', error);
    }
  }

  /**
   * Download all data from Firebase to localStorage (backup/sync)
   */
  async downloadFromFirebase() {
    if (!db) {
      throw new Error('Firebase is not initialized. Please check your configuration.');
    }

    const results = {
      success: [],
      errors: [],
      totalDownloaded: 0
    };

    toast.loading('Downloading data from Firebase...', { id: 'download' });

    try {
      for (const collectionName of this.collections) {
        try {
          const querySnapshot = await getDocs(collection(db, collectionName));
          const data = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          if (data.length > 0) {
            localStorage.setItem(collectionName, JSON.stringify(data));
            results.success.push({
              collection: collectionName,
              count: data.length
            });
            results.totalDownloaded += data.length;
          }
        } catch (error) {
          console.error(`Error downloading ${collectionName}:`, error);
          results.errors.push({
            collection: collectionName,
            error: error.message
          });
        }
      }

      toast.success(`Download completed! ${results.totalDownloaded} records downloaded.`, {
        id: 'download',
        duration: 5000
      });

      return results;
    } catch (error) {
      toast.error(`Download failed: ${error.message}`, { id: 'download' });
      throw error;
    }
  }

  /**
   * Get data from localStorage for a collection
   */
  getLocalStorageData(collectionName) {
    try {
      const data = localStorage.getItem(collectionName);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error(`Error reading localStorage for ${collectionName}:`, error);
      return [];
    }
  }

  /**
   * Create backup of localStorage data
   */
  createLocalBackup() {
    const backup = {};
    const timestamp = new Date().toISOString();

    for (const collectionName of this.collections) {
      const data = this.getLocalStorageData(collectionName);
      if (data && data.length > 0) {
        backup[collectionName] = data;
      }
    }

    // Add settings
    const settings = localStorage.getItem('clinicSettings');
    if (settings) {
      backup.settings = JSON.parse(settings);
    }

    // Save backup to localStorage with timestamp
    const backupKey = `clinicflow_backup_${timestamp}`;
    localStorage.setItem(backupKey, JSON.stringify(backup));

    toast.success(`Backup created: ${backupKey}`);
    return backupKey;
  }

  /**
   * Restore from backup
   */
  restoreFromBackup(backupKey) {
    try {
      const backup = localStorage.getItem(backupKey);
      if (!backup) {
        throw new Error('Backup not found');
      }

      const backupData = JSON.parse(backup);

      for (const [collectionName, data] of Object.entries(backupData)) {
        if (collectionName === 'settings') {
          localStorage.setItem('clinicSettings', JSON.stringify(data));
        } else {
          localStorage.setItem(collectionName, JSON.stringify(data));
        }
      }

      toast.success('Backup restored successfully');

      // Reload the page to refresh all components
      window.location.reload();
    } catch (error) {
      toast.error(`Restore failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * List all available backups
   */
  listBackups() {
    const backups = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('clinicflow_backup_')) {
        const timestamp = key.replace('clinicflow_backup_', '');
        backups.push({
          key,
          timestamp: new Date(timestamp),
          size: localStorage.getItem(key)?.length || 0
        });
      }
    }
    return backups.sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Delete a backup
   */
  deleteBackup(backupKey) {
    localStorage.removeItem(backupKey);
    toast.success('Backup deleted');
  }

  /**
   * Check Firebase connection
   */
  async checkFirebaseConnection() {
    try {
      if (!db) {
        return { connected: false, error: 'Firebase not initialized' };
      }

      // Try to read from a test collection
      await getDocs(collection(db, 'test'));
      return { connected: true };
    } catch (error) {
      return { connected: false, error: error.message };
    }
  }

  /**
   * Get migration status
   */
  getMigrationStatus() {
    const status = {
      collections: {},
      totalLocalRecords: 0,
      lastMigration: null
    };

    for (const collectionName of this.collections) {
      const localData = this.getLocalStorageData(collectionName);
      status.collections[collectionName] = {
        localCount: localData.length,
        hasData: localData.length > 0
      };
      status.totalLocalRecords += localData.length;
    }

    // Check for migration timestamp
    const migrationInfo = localStorage.getItem('lastFirebaseMigration');
    if (migrationInfo) {
      status.lastMigration = JSON.parse(migrationInfo);
    }

    return status;
  }

  /**
   * Save migration info
   */
  saveMigrationInfo(results) {
    const migrationInfo = {
      timestamp: new Date().toISOString(),
      results,
      dataSource: process.env.REACT_APP_DATA_SOURCE || 'local'
    };
    localStorage.setItem('lastFirebaseMigration', JSON.stringify(migrationInfo));
  }
}

export default new FirebaseMigration();
